import { useProfile } from "@hooks/crud/profile/useProfile";
import { PageLoader } from "@ui/molecules/Loaders/PageLoader";
import { THEME_STORAGE_KEYS } from "@themes/storageKeys";
import { browserStorage } from "@utils/browserStorage";
import {
  hasPendingOAuthCode,
  MFA_ENROLMENT_REQUIRED_EVENT,
  SESSION_EXPIRED_EVENT,
  SESSION_EXPIRED_MESSAGE
} from "@utils/authErrors";
import {
  catchSentryError,
  clearSentryUser,
  setSentryUser
} from "@utils/sentry";
import { showToast, ToastType } from "@utils/toast";
import { sleep } from "@utils/utils";
import {
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  signInWithRedirect,
  updatePassword
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import * as React from "react";
import { ProfileLineItem } from "catchment-common/interfaces/profile";
import { UserLineItem } from "catchment-common/interfaces/user";
import { formattedUserName } from "catchment-common/utils/string";

import {
  IS_PROD_ENV,
  isBMD,
  isDelta,
  isGoodman,
  isKapitol,
  isMultiplex,
  isSEC,
  isSymal
} from "@app/bootstrap/init";

const getProvider = () => {
  if (isBMD()) {
    return "BMD-SAML-IDP";
  } else if (isDelta()) {
    return "DELTA-SAML-IDP";
  } else if (isMultiplex()) {
    return "MULTIPLEX-SAML-IDP";
  } else if (isSymal()) {
    return "SYMAL-SAML-IDP";
  } else if (isKapitol()) {
    return "KAPITOL-SAML-IDP";
  } else if (isSEC()) {
    return "SEC-SAML-IDP";
  } else if (isGoodman()) {
    return "GOODMAN-SAML-IDP";
  }
  throw new Error("Unsupported provider");
};

/** Upper bound on waiting for Amplify to finish a hosted-UI code exchange. */
const OAUTH_CALLBACK_TIMEOUT_MS = 10_000;

/**
 * Refresh failures that actually mean the refresh token is dead.
 *
 * Amplify dispatches `tokenRefresh_failure` for EVERY failed refresh — a
 * dropped connection, a proxy reset, a 5xx — and for anything outside this set
 * it deliberately KEEPS the tokens so a later attempt can succeed
 * (TokenOrchestrator.handleErrors → isAuthenticationError, amplify-js#14534).
 * So treating them all as terminal wiped refresh tokens that were still valid.
 *
 * Mirrors Amplify's list exactly. Prefix-matched, as Amplify does — the names
 * arrive suffixed in some SDK paths.
 */
const TERMINAL_REFRESH_ERRORS = [
  "NotAuthorizedException",
  "TokenRevokedException",
  "RefreshTokenReuseException",
  "UserNotFoundException",
  "PasswordResetRequiredException",
  "UserNotConfirmedException"
];

const refreshErrorName = (error: unknown): string =>
  String((error as { name?: unknown } | undefined)?.name ?? "");

const isTerminalRefreshError = (error: unknown): boolean =>
  TERMINAL_REFRESH_ERRORS.some((terminal) =>
    refreshErrorName(error).startsWith(terminal)
  );

export enum AuthenticateChallenge {
  NONE = "NONE",
  NEW_PASSWORD_REQUIRED = "NEW_PASSWORD_REQUIRED",
  // Enrolled user in an MFA org: Cognito is asking for the 6-digit TOTP code.
  TOTP_REQUIRED = "TOTP_REQUIRED",
  // Un-enrolled user whose org requires MFA: must set up TOTP (QR) before entry.
  TOTP_SETUP_REQUIRED = "TOTP_SETUP_REQUIRED",
  AUTH_ERROR = "AUTH_ERROR",
  PROFILE_ERROR = "PROFILE_ERROR"
}

type AuthSessionPayload = {
  email?: string;
  "cognito:groups"?: string[];
  "cognito:username"?: string;
};

type AC = {
  loggedIn: boolean;
  isFirstLoad: boolean;
  user?: UserLineItem;
  isAuthLoading: boolean;
  userProfile?: ProfileLineItem;
  isContractor: boolean;
  isSocialProAdmin: boolean;
  setUserProfile: (profile: ProfileLineItem) => void;
  isAuthenticated: () => Promise<boolean>;
  // True when an already-logged-in user's org has enabled MFA and they haven't
  // enrolled: the backend 403s their requests and the API layer dispatches an
  // event that flips this. The app shell renders the forced enrolment screen
  // over the app until enrolment completes. NOT a sign-out (they keep their
  // session). Cleared by completeMfaSignIn.
  mfaEnrolmentRequired: boolean;
  // Called by the MFA screens once Cognito's TOTP challenge/setup has completed
  // (confirmSignIn / verifyTOTPSetup succeeded) to hydrate the user and flip
  // loggedIn — the same transition signIn does on its DONE path.
  completeMfaSignIn: () => Promise<boolean>;
  federatedSignIn: () => Promise<void>;
  signIn: (
    username: string,
    password: string
  ) => Promise<AuthenticateChallenge>;
  signUp: (
    username: string,
    password: string,
    isContractor: boolean
  ) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  forgotPasswordSubmit: (
    username: string,
    code: string,
    newPassword: string
  ) => Promise<string>;
  resetPassword: (
    username: string,
    oldPassword: string,
    newPassword: string
  ) => Promise<AuthenticateChallenge>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
};

export const AuthContext = React.createContext<AC>({
  changePassword: () => Promise.resolve(),
  completeMfaSignIn: () => Promise.resolve(false),
  federatedSignIn: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  forgotPasswordSubmit: () => Promise.resolve(""),
  isAuthenticated: () => Promise.resolve(false),
  isAuthLoading: false,
  isContractor: false,
  isFirstLoad: false,
  isSocialProAdmin: false,
  loggedIn: false,
  mfaEnrolmentRequired: false,
  resetPassword: () => Promise.resolve(AuthenticateChallenge.NONE),
  setUserProfile: () => {
    return;
  },
  signIn: () => Promise.resolve(AuthenticateChallenge.NONE),
  signOut: () => Promise.resolve(),
  signUp: () => Promise.resolve()
});

type AuthProviderProps = {
  children: React.ReactNode;
};

const AuthProvider = (props: AuthProviderProps) => {
  const [user, setUser] = React.useState<UserLineItem>();
  const [loggedIn, setLoggedIn] = React.useState(false);
  const [isAuthLoading, setIsAuthLoading] = React.useState(true);
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);
  const { getProfile } = useProfile();
  const [userProfile, setUserProfile] = React.useState<ProfileLineItem>();
  const [mfaEnrolmentRequired, setMfaEnrolmentRequired] = React.useState(false);

  // The session payload, or undefined when the user simply has no session.
  // Signed-out is the ordinary state on /login and on the app's own boot check,
  // so it must be answerable without throwing.
  const tryGetSessionPayload = async (): Promise<
    AuthSessionPayload | undefined
  > => {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.payload as AuthSessionPayload | undefined;
  };

  // Same, but for the callers that run *after* Cognito has confirmed a sign-in.
  // A missing session there is a real fault and the throw is load-bearing —
  // swallowing it once let the UI show "Password reset!" to a user who had no
  // session (13-F). Do not soften this; use tryGetSessionPayload instead.
  const getSessionPayload = async (): Promise<AuthSessionPayload> => {
    const payload = await tryGetSessionPayload();
    if (!payload) {
      throw new Error("User not configured correctly.");
    }
    return payload;
  };

  const getEmail = (payload: AuthSessionPayload) => {
    return payload.email || "";
  };

  const getUserId = (payload: AuthSessionPayload) => {
    const userId = payload["cognito:username"] as string;
    if (!userId) {
      throw new Error("User userId id not available");
    }
    return formattedUserName(userId);
  };

  const getGroups = (payload: AuthSessionPayload) => {
    const groups = payload["cognito:groups"];
    const isContractor = !!groups?.includes("Contractor");
    const isSocialProAdmin = !!groups?.includes("SocialProAdmin");
    return { isContractor, isSocialProAdmin };
  };

  // Single source of truth for "must this user be forced into TOTP setup before
  // entering the app?" — used by both login and reset-password so the security
  // gate cannot drift between them. Federated (IdP owns MFA) and SocialProAdmin
  // (platform role that jumps orgs) users are exempt, mirroring the backend guard.
  const requiresMfaEnrolment = (
    profile: ProfileLineItem | undefined,
    payload: AuthSessionPayload
  ): boolean =>
    !!profile?.organisationMfaEnabled &&
    !profile.mfaEnrolled &&
    !profile.federatedUser &&
    !getGroups(payload).isSocialProAdmin;

  // Retry the first authenticated profile fetch on a transient failure.
  // getProfile swallows errors (e.g. Amplify's REST config not yet resolved →
  // InvalidApiName, seen on Safari) and returns { success: false } instead of
  // throwing, so retrying must key off the failed result, not a thrown error.
  const getProfileWithRetry = React.useCallback(
    async (userId: string) => {
      const maxRetries = 3,
        delay = 1000;
      let profile = await getProfile(userId, true);
      for (
        let attempt = 1;
        attempt < maxRetries && !profile.success;
        attempt++
      ) {
        await sleep(delay);
        profile = await getProfile(userId, true);
      }
      return profile;
    },
    [getProfile]
  );

  const isAuthenticated = React.useCallback(async () => {
    try {
      const payload = await tryGetSessionPayload();
      // No session: an anonymous visitor, not a fault. Reporting this was the
      // single widest-reaching Sentry issue in the project (FRONTEND-ZT).
      if (!payload) {
        return false;
      }
      const userId = getUserId(payload);
      const profile = await getProfileWithRetry(userId);
      if (!profile.success) {
        return false;
      }
      setUser({
        email: getEmail(payload),
        id: userId,
        ...getGroups(payload)
      });
      if (profile.profile) {
        setUserProfile(profile.profile);
      }
      return true;
    } catch (e) {
      catchSentryError(e, true);
      return false;
    } finally {
      setIsAuthLoading(false);
    }
  }, [getProfileWithRetry]);

  const federatedSignIn = React.useCallback(async (): Promise<void> => {
    try {
      const provider = getProvider();
      await signInWithRedirect({ provider: { custom: provider } });
    } catch (e) {
      catchSentryError(e, true);
    }
  }, []);

  const completeMfaSignIn = React.useCallback(async (): Promise<boolean> => {
    const authed = await isAuthenticated();
    if (authed) {
      setLoggedIn(true);
      // Enrolment done — clear the mid-session gate so the app resumes.
      setMfaEnrolmentRequired(false);
    }
    return authed;
  }, [isAuthenticated]);

  const signIn = React.useCallback(
    async (
      username: string,
      password: string
    ): Promise<AuthenticateChallenge> => {
      const hydrateAuthenticatedUser =
        async (): Promise<AuthenticateChallenge> => {
          const payload = await getSessionPayload();
          const userId = getUserId(payload);
          try {
            const profile = await getProfileWithRetry(userId);
            if (!profile.success) {
              return AuthenticateChallenge.PROFILE_ERROR;
            }
            // Org requires MFA and this user hasn't enrolled: gate into forced
            // TOTP setup instead of completing login. Catches the case where the
            // org flag was enabled after they had an account but before they
            // enrolled.
            if (requiresMfaEnrolment(profile.profile, payload)) {
              if (profile.profile) {
                setUserProfile(profile.profile);
              }
              return AuthenticateChallenge.TOTP_SETUP_REQUIRED;
            }
            if (profile.profile) {
              setUserProfile(profile.profile);
            }
            if (profile.success) {
              setUser({
                email: getEmail(payload),
                id: userId,
                ...getGroups(payload)
              });
              setLoggedIn(true);
            }
          } catch (e) {
            catchSentryError(e, true);
            return AuthenticateChallenge.PROFILE_ERROR;
          }
          return AuthenticateChallenge.NONE;
        };

      const attemptSignIn = async (): Promise<AuthenticateChallenge> => {
        const res = await amplifySignIn({ password, username });
        switch (res.nextStep.signInStep) {
          case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
            return AuthenticateChallenge.NEW_PASSWORD_REQUIRED;
          case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
            // Enrolled user — Cognito wants the 6-digit code.
            return AuthenticateChallenge.TOTP_REQUIRED;
          case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
            // Cognito-driven TOTP setup (only if pool were REQUIRED); handled
            // by the same enrolment screen.
            return AuthenticateChallenge.TOTP_SETUP_REQUIRED;
          case "DONE":
            return await hydrateAuthenticatedUser();
          default:
            // Any other step we don't handle — hydrate and let the profile-based
            // gate (above) decide, rather than dead-ending on an error.
            return await hydrateAuthenticatedUser();
        }
      };

      try {
        return await attemptSignIn();
      } catch (e: any) {
        if (e?.name === "UserAlreadyAuthenticatedException") {
          // The login form is the only caller, so a password was just typed:
          // an explicit request for a NEW session. Resuming the stored one
          // ignored those credentials entirely — if it belonged to someone
          // else (a shared machine, an interrupted sign-out) it signed this
          // user in as them, and if it was broken they looped here forever,
          // since the password was never checked and a reset could not help.
          // Discard it and honour what they typed.
          try {
            await amplifySignOut();
            return await attemptSignIn();
          } catch (retryError) {
            catchSentryError(retryError, true);
            return AuthenticateChallenge.AUTH_ERROR;
          }
        }
        catchSentryError(e, true);
        return AuthenticateChallenge.AUTH_ERROR;
      }
    },
    [getProfileWithRetry]
  );

  const signUp = React.useCallback(
    async (email: string, password: string): Promise<void> => {
      if (IS_PROD_ENV) {
        throw new Error("Not implemented");
      }
      try {
        await amplifySignUp({
          options: {
            userAttributes: {
              email
            }
          },
          password,
          username: email
        });
      } catch (error) {
        catchSentryError(error, false, {
          tags: { operation: "user_signup" }
        });
      }
    },
    []
  );

  const changePassword = React.useCallback(
    async (oldPassword: string, newPassword: string): Promise<void> => {
      try {
        await updatePassword({ newPassword, oldPassword });
      } catch (e) {
        catchSentryError(e, true);
        throw e;
      }
    },
    []
  );

  const resetPassword = React.useCallback(
    async (
      username: string,
      oldPassword: string,
      newPassword: string
    ): Promise<AuthenticateChallenge> => {
      try {
        const signInResult = await amplifySignIn({
          password: oldPassword,
          username
        });
        if (
          signInResult.nextStep.signInStep ===
          "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          // Setting the new password may not complete sign-in: in an MFA org
          // Cognito's next step is a TOTP challenge/setup. Inspect the result
          // and do NOT assume a session exists (13-F: previously this fell
          // through to getSessionPayload(), threw, was swallowed, and the UI
          // showed a false "Password reset!" while the user had no session).
          const confirmResult = await confirmSignIn({
            challengeResponse: newPassword
          });
          switch (confirmResult.nextStep.signInStep) {
            case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
              return AuthenticateChallenge.TOTP_REQUIRED;
            case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
              return AuthenticateChallenge.TOTP_SETUP_REQUIRED;
            case "DONE":
              break;
            default:
              break;
          }
        }
        const payload = await getSessionPayload();
        const userId = getUserId(payload);
        const profile = await getProfileWithRetry(userId);
        // Password is set and the user is signed in — but the org may require
        // MFA enrolment before entry. Gate the same way login does.
        if (requiresMfaEnrolment(profile.profile, payload)) {
          if (profile.profile) {
            setUserProfile(profile.profile);
          }
          return AuthenticateChallenge.TOTP_SETUP_REQUIRED;
        }
        if (profile.profile && profile.success) {
          setUserProfile(profile.profile);
        }
        if (profile.success) {
          setUser({
            email: getEmail(payload),
            id: userId,
            ...getGroups(payload)
          });
          setLoggedIn(true);
        }
        return AuthenticateChallenge.NONE;
      } catch (e) {
        catchSentryError(e, true);
        return AuthenticateChallenge.AUTH_ERROR;
      }
    },
    [getProfileWithRetry]
  );

  const forgotPassword = React.useCallback(
    async (username: string): Promise<void> => {
      try {
        await amplifyResetPassword({ username });
      } catch (e) {
        catchSentryError(e, true);
      }
    },
    []
  );

  const forgotPasswordSubmit = React.useCallback(
    async (
      username: string,
      code: string,
      newPassword: string
    ): Promise<string> => {
      try {
        await confirmResetPassword({
          confirmationCode: code,
          newPassword,
          username
        });
        return "SUCCESS";
      } catch (e) {
        catchSentryError(e, true);
        return "";
      }
    },
    []
  );

  const signOut = React.useCallback(async () => {
    try {
      setIsAuthLoading(true);
      await amplifySignOut();
      // Not `localStorage.clear()`: that also wiped the theme mode and accent,
      // which are browser preferences rather than session state, so signing out
      // (or being bounced by a failed token refresh) reset the user's theme.
      browserStorage.clearExcept(THEME_STORAGE_KEYS);
      setLoggedIn(false);
      setUser(undefined);
      setUserProfile(undefined);
      // Clear Sentry user context on sign out
      clearSentryUser();
    } catch (e) {
      catchSentryError(e, true);
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  // Sign the user out and bounce to login when their session is terminally
  // unauthorized — either signalled by the API layer (a 401 that survived a
  // forced token refresh) or by Amplify failing to refresh the token with an
  // error that means the refresh token itself is dead (see
  // TERMINAL_REFRESH_ERRORS; a transient failure is not an expiry).
  // We only react to `tokenRefresh_failure`, not `signedOut`, since the latter
  // is emitted by our own signOut and would loop. FRONTEND-YK.
  //
  // Both signals can fire for the same expiry (the API event and Amplify's Hub
  // event), so a `handled` latch guarantees exactly one toast and one signOut.
  // This is also the single place that owns the user-facing expiry message:
  // the API layer mutes its own toast (handleApiError passes muteToast=true), so
  // surfacing it here is what makes the user actually see why they were logged
  // out, regardless of which signal arrived first.
  React.useEffect(() => {
    let handled = false;
    let transientReported = false;
    const handleSessionExpired = () => {
      if (handled) {
        return;
      }
      handled = true;
      showToast(SESSION_EXPIRED_MESSAGE, ToastType.Error);
      void signOut();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    const hubUnsubscribe = Hub.listen("auth", ({ payload }) => {
      // A refresh got through, so the next failure is a new incident rather
      // than more of the one already reported. Without this the latch below is
      // once per PAGE LOAD: a blip at 09:00 that recovered on its own would
      // silence a real outage at 15:00, keeping the report that mattered least.
      if (payload.event === "tokenRefresh") {
        transientReported = false;
      }
      if (payload.event === "tokenRefresh_failure") {
        const error = payload.data?.error;
        if (isTerminalRefreshError(error)) {
          handleSessionExpired();
        } else if (!transientReported) {
          // Transient: keep the session (see TERMINAL_REFRESH_ERRORS). Reported
          // once per incident — a flapping connection fails every refresh, and
          // one event per flap is noise.
          //
          // Wrapped rather than passed through: the failure this is here to
          // catch arrives as `NetworkError` / "A network error has occurred",
          // which shouldDropSentryEvent discards before it reaches Sentry, so
          // reporting the error itself would report nothing at all. The name
          // travels in the message; the original hangs off `cause`.
          transientReported = true;
          catchSentryError(
            new Error(
              `Token refresh failed: ${refreshErrorName(error) || "unknown"}`,
              { cause: error }
            ),
            true,
            { tags: { operation: "token_refresh" } }
          );
        }
      }
      // A federated sign-in that never completed. Not an expiry — do NOT sign
      // out, the user has no session to lose. Reporting it is the whole point:
      // this failure was previously silent, so a broken SAML tenant just
      // bounced its users around the IdP with nothing in Sentry to explain it.
      if (payload.event === "signInWithRedirect_failure") {
        catchSentryError(payload.data?.error, true, {
          tags: { operation: "federated_sign_in" }
        });
      }
    });
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      hubUnsubscribe();
    };
  }, [signOut]);

  // Mid-session MFA gate: the API layer dispatches this when a logged-in user's
  // org enabled MFA but they haven't enrolled (backend 403). Flip the flag so
  // the app shell shows forced enrolment. Do NOT sign them out.
  React.useEffect(() => {
    const handleMfaRequired = () => setMfaEnrolmentRequired(true);
    window.addEventListener(MFA_ENROLMENT_REQUIRED_EVENT, handleMfaRequired);
    return () =>
      window.removeEventListener(
        MFA_ENROLMENT_REQUIRED_EVENT,
        handleMfaRequired
      );
  }, []);

  // Sync user profile with Sentry for better error tracking
  React.useEffect(() => {
    if (userProfile && user) {
      setSentryUser({
        email: user.email,
        id: userProfile.id,
        organisationId: userProfile.organisationId,
        username: formattedUserName(
          `${userProfile.firstName} ${userProfile.lastName}`
        )
      });
    } else if (!userProfile) {
      clearSentryUser();
    }
  }, [userProfile, user]);

  React.useEffect(() => {
    const check = () =>
      isAuthenticated()
        .then((res) => {
          setLoggedIn(res);
          setIsAuthLoading(false);
        })
        .finally(() => {
          setIsFirstLoad(false);
        });

    // Ordinary load: no redirect in flight, just ask.
    if (!hasPendingOAuthCode()) {
      void check();
      return;
    }

    // Hosted-UI callback. Amplify exchanges the `?code=` for tokens
    // asynchronously after configure(), so asking now can report "signed out"
    // for a user who just signed in. The app then routes to /login — which
    // drops ?code= from the URL and re-enters signInWithRedirect against a
    // still-live IdP session, aborting the exchange mid-flight and doing it
    // all again. That is the BMD loop: authorize, token, discard, repeat.
    // Wait for Amplify to say the redirect is finished before asking.
    let done = false;
    const finish = () => {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      unsubscribe();
      void check();
    };
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (
        payload.event === "signInWithRedirect" ||
        payload.event === "signInWithRedirect_failure"
      ) {
        finish();
      }
    });
    // Neither event is guaranteed — an Amplify version that already resolved
    // the exchange before this listener attached emits nothing. Falling back to
    // the plain check keeps a normal load from hanging on the loader forever.
    const timer = setTimeout(finish, OAUTH_CALLBACK_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  if (isFirstLoad) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider
      value={{
        changePassword,
        completeMfaSignIn,
        federatedSignIn,
        forgotPassword,
        forgotPasswordSubmit,
        isAuthenticated,
        isAuthLoading,
        isContractor: user?.isContractor || false,
        isFirstLoad,
        isSocialProAdmin: user?.isSocialProAdmin || false,
        loggedIn,
        mfaEnrolmentRequired,
        resetPassword,
        setUserProfile,
        signIn,
        signOut,
        signUp,
        user,
        userProfile
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => React.useContext(AuthContext);

export { AuthProvider, useAuthContext };
